import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '@store/store';

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v2',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['EC2', 'RDS', 'S3', 'URLs', 'User', 'Dashboard'],
  endpoints: (builder) => ({
    // Dashboard
    getDashboardMetrics: builder.query({
      query: () => '/metrics/dashboard',
      providesTags: ['Dashboard'],
    }),
    
    // EC2
    getEC2Instances: builder.query({
      query: (params) => ({
        url: '/ec2/instances',
        params,
      }),
      providesTags: ['EC2'],
    }),
    
    getEC2Instance: builder.query({
      query: (id) => `/ec2/instances/${id}`,
      providesTags: ['EC2'],
    }),
    
    getEC2Metrics: builder.query({
      query: ({ id, ...params }) => ({
        url: `/ec2/instances/${id}/metrics`,
        params,
      }),
      providesTags: ['EC2'],
    }),
    
    // RDS
    getRDSInstances: builder.query({
      query: (params) => ({
        url: '/rds/instances',
        params,
      }),
      providesTags: ['RDS'],
    }),
    
    getRDSInstance: builder.query({
      query: (id) => `/rds/instances/${id}`,
      providesTags: ['RDS'],
    }),
    
    getRDSMetrics: builder.query({
      query: ({ id, ...params }) => ({
        url: `/rds/instances/${id}/metrics`,
        params,
      }),
      providesTags: ['RDS'],
    }),
    
    // S3
    getS3Buckets: builder.query({
      query: (params) => ({
        url: '/s3/buckets',
        params,
      }),
      providesTags: ['S3'],
    }),
    
    getS3Bucket: builder.query({
      query: (name) => `/s3/buckets/${name}`,
      providesTags: ['S3'],
    }),
    
    getS3Metrics: builder.query({
      query: (name) => `/s3/buckets/${name}/metrics`,
      providesTags: ['S3'],
    }),
    
    updateS3Quota: builder.mutation({
      query: ({ name, quota }) => ({
        url: `/s3/buckets/${name}/quota`,
        method: 'PUT',
        body: quota,
      }),
      invalidatesTags: ['S3'],
    }),
    
    // URLs
    getURLMonitors: builder.query({
      query: (params) => ({
        url: '/urls',
        params,
      }),
      providesTags: ['URLs'],
    }),
    
    getURLMonitor: builder.query({
      query: (id) => `/urls/${id}`,
      providesTags: ['URLs'],
    }),
    
    createURLMonitor: builder.mutation({
      query: (data) => ({
        url: '/urls',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['URLs'],
    }),
    
    updateURLMonitor: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/urls/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['URLs'],
    }),
    
    deleteURLMonitor: builder.mutation({
      query: (id) => ({
        url: `/urls/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['URLs'],
    }),
    
    checkURL: builder.mutation({
      query: (id) => ({
        url: `/urls/${id}/check`,
        method: 'POST',
      }),
      invalidatesTags: ['URLs'],
    }),
  }),
});

export const {
  useGetDashboardMetricsQuery,
  useGetEC2InstancesQuery,
  useGetEC2InstanceQuery,
  useGetEC2MetricsQuery,
  useGetRDSInstancesQuery,
  useGetRDSInstanceQuery,
  useGetRDSMetricsQuery,
  useGetS3BucketsQuery,
  useGetS3BucketQuery,
  useGetS3MetricsQuery,
  useUpdateS3QuotaMutation,
  useGetURLMonitorsQuery,
  useGetURLMonitorQuery,
  useCreateURLMonitorMutation,
  useUpdateURLMonitorMutation,
  useDeleteURLMonitorMutation,
  useCheckURLMutation,
} = apiSlice;